"use client";

import { motion } from "framer-motion";
import Image from "next/image";

interface QuoteBlockProps {
  quote: string;
  author?: string;
  authorTitle?: string;
  image?: string;
  imageAlt?: string;
  className?: string;
}

/**
 * QuoteBlock Component
 * 
 * A professional quote block with dark thematic background,
 * large decorative quote marks, and an optional image on the right.
 * 
 * Usage:
 * <QuoteBlock 
 *   quote="Your success is our priority..."
 *   author="John Doe"
 *   authorTitle="CEO, Company"
 *   image="/path/to/image.jpg"
 * />
 */
export function QuoteBlock({
  quote,
  author,
  authorTitle,
  image,
  imageAlt = "Quote illustration",
  className = "",
}: QuoteBlockProps) {
  return (
    <div 
      className={`w-full ${className}`}
      style={{ backgroundColor: 'var(--homepage-primary, #0d2434)' }}
    >
      <div className={`grid ${image ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'} ${image ? '' : 'py-16 sm:py-20 lg:py-24'}`}>
        
        {/* Left Column - Quote Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className={`relative flex flex-col justify-center ${image ? 'py-12 sm:py-16 lg:py-20 px-12 sm:px-16 lg:px-24 xl:px-32' : 'py-16 sm:py-20 lg:py-24 px-12 sm:px-16 lg:px-24 xl:px-32'}`}
        >
          {/* Large decorative opening quote mark */}
          <div 
            className="absolute top-4 sm:top-6 lg:top-8 left-8 sm:left-12 lg:left-20 text-[5rem] sm:text-[7rem] lg:text-[9rem] leading-none font-serif pointer-events-none select-none opacity-50"
            style={{ color: '#14b8a6' }}
            aria-hidden="true"
          >
            &ldquo;
          </div>
            
            {/* Quote text */}
            <blockquote className="relative z-10 pt-10 sm:pt-14 lg:pt-18">
              <p className="text-base sm:text-lg lg:text-xl xl:text-2xl font-light text-white/95 leading-relaxed w-full">
                {quote}
              </p>
              
              {/* Closing quote mark inline */}
              <span 
                className="inline-block text-3xl sm:text-4xl lg:text-5xl font-serif ml-1 sm:ml-2 align-middle opacity-50"
                style={{ color: '#14b8a6' }}
                aria-hidden="true"
              >
                &rdquo;
              </span>
            </blockquote>
            
            {/* Author attribution */}
            {(author || authorTitle) && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.2 }}
                viewport={{ once: true }}
                className="mt-8 sm:mt-10 flex items-center gap-3 sm:gap-4 w-full"
              >
                {/* Accent line */}
                <div 
                  className="w-10 sm:w-12 h-0.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: '#14b8a6' }}
                />
                <div>
                  {author && (
                    <p className="text-sm sm:text-base lg:text-lg font-semibold text-white">
                      {author}
                    </p>
                  )}
                  {authorTitle && (
                    <p className="text-xs sm:text-sm lg:text-base text-white/70">
                      {authorTitle}
                    </p>
                  )}
                </div>
              </motion.div>
            )}
          </motion.div>

          {/* Right Column - Image (optional) */}
          {image && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="relative w-full h-full min-h-[350px] sm:min-h-[450px] lg:min-h-0"
            >
              <Image
                src={image}
                alt={imageAlt}
                fill
                className="object-cover"
                sizes="(max-width: 1024px) 100vw, 50vw"
                priority
              />
              {/* Subtle overlay for visual cohesion */}
              <div 
                className="absolute inset-0 bg-gradient-to-tr" 
                style={{ 
                  backgroundImage: 'linear-gradient(to top right, rgba(13, 36, 52, 0.3), transparent)' 
                }}
              />
            </motion.div>
          )}
        </div>
      </div>
  );
}
